import type { SvgIconComponent } from '@mui/icons-material'
import type { ListItemProps, SxProps, Theme } from '@mui/material'
import { Box, Button, Collapse, ListItem } from '@mui/material'
import { alpha } from '@mui/material/styles'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { NavLink as RouterLink } from 'react-router-dom'
import ChevronRightIcon from '../../icons/ChevronRight'

interface NavItemProps extends ListItemProps {
  active?: boolean
  activeIcon?: SvgIconComponent
  children?: ReactNode
  depth: number
  icon?: SvgIconComponent
  info?: ReactNode
  open?: boolean
  path?: string
  title?: string
}

const PRIMARY_TEXT_COLOR = 'rgb(66, 82, 110)'
const CHILD_INDENT = '36px'

const getButtonSx = ({
  active,
  depth,
  isBranch,
  isCollapsed,
}: {
  active: boolean
  depth: number
  isBranch: boolean
  isCollapsed: boolean
}): SxProps<Theme> => {
  const isChild = depth > 0
  const isActiveLeaf = active && !isBranch

  return {
    minHeight: isChild ? 38 : 44,
    py: 0,
    pl: isCollapsed ? 0 : isChild ? '12px' : '16px',
    pr: isCollapsed ? 0 : '12px',
    width: '100%',
    minWidth: 0,
    borderRadius: '8px',
    justifyContent: isCollapsed ? 'center' : 'flex-start',
    textAlign: 'left',
    textTransform: 'none',
    fontSize: 14,
    fontWeight: isChild ? 500 : 600,
    lineHeight: 1.3,
    color: PRIMARY_TEXT_COLOR,
    '& .MuiButton-startIcon': {
      ml: 0,
      mr: isCollapsed ? 0 : '12px',
    },
    '&:hover': {
      bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04),
    },
    ...(active &&
      isBranch && {
        color: 'text.primary',
        fontWeight: 600,
      }),
    ...(isActiveLeaf && {
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
      color: 'primary.dark',
      fontWeight: 600,
      '&:hover': {
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
      },
    }),
  }
}

const NavItem = (props: NavItemProps) => {
  const {
    active = false,
    activeIcon,
    children,
    depth,
    icon,
    info,
    open: openProp,
    path,
    title,
    ...other
  } = props
  const [open, setOpen] = useState<boolean>(!!openProp)
  const isCollapsed = !title
  const Icon = active && activeIcon ? activeIcon : icon
  const startIcon = Icon && <Icon fontSize='small' />

  const handleToggle = (): void => {
    setOpen((prevOpen) => !prevOpen)
  }

  // Branch
  if (children) {
    return (
      <ListItem
        disableGutters
        sx={{
          display: 'block',
          py: 0,
          mb: open ? '8px' : 0,
          transition: 'margin 150ms ease',
        }}
        {...other}
      >
        <Button
          onClick={handleToggle}
          startIcon={startIcon}
          sx={getButtonSx({ active, depth, isBranch: true, isCollapsed })}
          variant='text'
        >
          <Box sx={{ flexGrow: 1 }}>{title}</Box>
          {info}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 16,
              ml: 1,
              color: 'text.disabled',
            }}
          >
            <ChevronRightIcon
              sx={{
                fontSize: 16,
                transform: open ? 'rotate(90deg)' : 'none',
                transition: 'transform 150ms ease',
              }}
            />
          </Box>
        </Button>
        <Collapse in={open}>
          <Box sx={{ pt: '2px' }}>{children}</Box>
        </Collapse>
      </ListItem>
    )
  }

  // Leaf
  return (
    <ListItem
      disableGutters
      sx={{
        display: 'flex',
        py: 0,
        pl: depth > 0 ? CHILD_INDENT : 0,
        '& + &': {
          mt: '2px',
        },
      }}
    >
      <Button
        component={path ? RouterLink : 'button'}
        startIcon={startIcon}
        sx={getButtonSx({ active, depth, isBranch: false, isCollapsed })}
        variant='text'
        to={path}
      >
        {!isCollapsed && <Box sx={{ flexGrow: 1 }}>{title}</Box>}
        {info}
      </Button>
    </ListItem>
  )
}

export default NavItem
